import type { DateRange } from '@plato/ui/components/calendar';
import { DateRangePicker } from '@plato/ui/components/date-picker';
import { cn } from '@plato/ui/util/classes';
import { endOfDay } from 'date-fns';
import { CalendarIcon, ChevronDownIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMonthShortDay, formatMonthShortDayYear } from '../../lib/date-helpers.ts';
import { useDateFnsLocale } from '../../lib/hooks/use-date-fns-locale.ts';
import { RadioList, RadioListItem } from '../ui/button-radio-list.tsx';
import { Button } from '../ui/button.tsx';
import { RelativeRangeInput } from './relative-range-input.tsx';
import type {
    DateFilterValue,
    DateRangeFilterOption,
    DateRangeValue,
    RelativeDateRange,
} from './types/filter-types.ts';
import { DateRangeComparison, RelativeTimeUnit } from './types/filter-types.ts';
import { isDateRangeValue, isRelativeDateRange } from './utils/date-filter-utils.ts';

// Local type definitions
// Three modes: newer (in the last X units), older (older than X units), range (from/to)
type FilterMode = 'newer' | 'older' | 'range';

type DateRangeFilterProps = {
    filter: DateRangeFilterOption;
    onChange: (value: DateFilterValue) => void;
    className?: string;
};

export function DateRangeFilter({ filter, onChange, className }: DateRangeFilterProps) {
    const { i18n, t } = useTranslation('filters');
    const locale = i18n.languages[0];
    const dateFnsLocalization = useDateFnsLocale(locale);

    // Internal state for the selected date range with proper type casting
    const [selectedDateRange, setSelectedDateRange] = useState<DateFilterValue>(() => {
        const range = filter.selectedRange || null;
        // Validate the incoming range matches our expected types
        if (isRelativeDateRange(range) || isDateRangeValue(range)) {
            return range;
        }
        return null;
    });

    // Set initial filter mode based on whether we have a date range selected
    const [filterMode, setFilterMode] = useState<FilterMode>(() => {
        if (isDateRangeValue(selectedDateRange) && selectedDateRange.from && selectedDateRange.to) {
            return 'range';
        }
        // If we have a relative range, infer newer/older from the comparison field
        if (isRelativeDateRange(selectedDateRange)) {
            return selectedDateRange.comparison === DateRangeComparison.OLDER ? 'older' : 'newer';
        }

        return 'newer';
    });

    const handleRelativeChange = useCallback(
        (newRelativeRange: RelativeDateRange) => {
            if (filterMode === 'range') {
                return;
            }

            const rangeWithComparison: RelativeDateRange = {
                ...newRelativeRange,
                comparison: filterMode === 'older' ? DateRangeComparison.OLDER : DateRangeComparison.NEWER,
            };

            setSelectedDateRange(rangeWithComparison);

            if (newRelativeRange.count > 0) {
                onChange(rangeWithComparison);
            }
        },
        [filterMode, onChange],
    );

    const handleModeChange = (mode: string) => {
        setFilterMode(mode as FilterMode);

        if (mode === 'range') {
            // Clear the filter when switching to custom range mode
            const newRange: DateFilterValue = {
                from: undefined,
                to: undefined,
            };
            setSelectedDateRange(newRange);
            return;
        }

        const comparison = mode === 'older' ? DateRangeComparison.OLDER : DateRangeComparison.NEWER;
        const base = isRelativeDateRange(selectedDateRange)
            ? selectedDateRange
            : { count: 0, unit: RelativeTimeUnit.DAYS };

        const newRelativeRange: RelativeDateRange = { ...base, comparison };

        setSelectedDateRange(newRelativeRange);
        onChange(newRelativeRange);
    };

    const handleDateRangeChange = (range: DateRange | undefined) => {
        if (range) {
            const newRange: DateRangeValue = { ...range, to: range.to ? endOfDay(range.to) : undefined };
            setSelectedDateRange(newRange);
            onChange(newRange);
        } else {
            const newRange: DateFilterValue = null;
            setSelectedDateRange(newRange);
            onChange(newRange);
        }
    };

    const formatDateRange = (): string => {
        if (!isDateRangeValue(selectedDateRange) || !selectedDateRange.from) {
            return t('date_range_filter__please_select');
        }

        const fromFormatted = formatMonthShortDay(selectedDateRange.from, locale);
        const toFormatted = selectedDateRange.to
            ? formatMonthShortDayYear(selectedDateRange.to, locale)
            : '...';

        return `${fromFormatted} - ${toFormatted}`;
    };

    const controlledRelativeValue: RelativeDateRange = isRelativeDateRange(selectedDateRange)
        ? selectedDateRange
        : {
              count: 0,
              unit: RelativeTimeUnit.DAYS,
              comparison: filterMode === 'older' ? DateRangeComparison.OLDER : DateRangeComparison.NEWER,
          };

    return (
        <div className={cn('flex flex-col space-y-3', className)}>
            {/* Main mode selection */}
            <RadioList value={filterMode} onValueChange={handleModeChange} className="flex flex-col">
                <RadioListItem value="newer" label={t('date_range_filter__newer_than')} />
                <RadioListItem value="older" label={t('date_range_filter__older_than')} />
                <RadioListItem value="range" label={t('date_range_filter__range_mode')} />
            </RadioList>

            {(filterMode === 'newer' || filterMode === 'older') && (
                <RelativeRangeInput value={controlledRelativeValue} onChange={handleRelativeChange} />
            )}

            {filterMode === 'range' && (
                <DateRangePicker
                    title={filter.label}
                    placeholder={t('date_range_filter__please_select')}
                    applyLabel={t('apply', { ns: 'common' })}
                    cancelLabel={t('form__cancel', { ns: 'common' })}
                    locale={dateFnsLocalization}
                    value={
                        isDateRangeValue(selectedDateRange)
                            ? { from: selectedDateRange.from, to: selectedDateRange.to }
                            : undefined
                    }
                    onValueChange={handleDateRangeChange}
                    render={
                        <Button variant="outline" className="w-full justify-between text-left font-normal">
                            <div className="flex items-center">
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                <span className="text-sm">{formatDateRange()}</span>
                            </div>
                            <ChevronDownIcon className="h-4 w-4 opacity-50" />
                        </Button>
                    }
                />
            )}
        </div>
    );
}
